import { PPLX_TRACKING_ALLOWED } from '#pplx/frontend/analytics/core.js';
import { MAX_DAYS, ONE_MONTH_IN_DAYS, PPLX_CHOSEN_LOCALE_COOKIE, PPLX_DEFAULT_SEARCH_SESSION_COOKIE, PPLX_DYNAMIC_COPY_COOKIE, PPLX_LOSS_AVERSION_MODAL_STATUS, PPLX_PATENTS_TUTORIAL_DISMISSED, PPLX_TASKS_SETTINGS_SEEN, PPLX_UTM_SOURCE_COOKIE, TEN_MINUTES_IN_DAYS, } from '#pplx/frontend/apps/ask/browser-state/cookieConstants.js';
import { edgeCompatibleSHA256HexDigest } from '#pplx/frontend/apps/ask/browser-state/edgeCompatibleSHA256HexDigest.js';
import { logWithTime, warnWithTime } from '#pplx/frontend/apps/ask/o11y/logging.js';
import { getAbsoluteUrl } from '#pplx/frontend/apps/ask/routing/cleanQueryRedirect.js';
import { boundGetRandomValues } from '#pplx/frontend/common/safeCrypto.js';
import { PPLX_SESSION_ID_COOKIE } from '#pplx/frontend/cookies/constants.js';
import { COOKIE_OPTION_KEYS, getCookie, removeCookie, setCookie, } from '#pplx/frontend/cookies/provider.js';
import { reportAction } from '#pplx/frontend/metrics/browser.js';
export const setLocaleCookie = (locale) => {
    // long expiration for your chosen locale
    setCookie(PPLX_CHOSEN_LOCALE_COOKIE, locale, {
        expires: MAX_DAYS,
    });
};
export const getLocaleCookie = () => {
    return getCookie(PPLX_CHOSEN_LOCALE_COOKIE);
};
export const removeLocaleCookie = () => {
    removeCookie(PPLX_CHOSEN_LOCALE_COOKIE);
};
export const setLossAversionModalStatusCookie = (status) => {
    setCookie(PPLX_LOSS_AVERSION_MODAL_STATUS, status, {});
};
export const getTrackingDisallowedCookie = () => {
    const trackingAllowed = getCookie(PPLX_TRACKING_ALLOWED) ?? true;
    return !trackingAllowed;
};
export const bumpSessionIdExpiration = () => {
    /**
     * Bumps the expiration of an existing session ID cookie to 10 minutes.
     */
    const sessionId = getCookie(PPLX_SESSION_ID_COOKIE);
    if (sessionId) {
        setCookie(PPLX_SESSION_ID_COOKIE, sessionId, {
            expires: TEN_MINUTES_IN_DAYS,
        });
    }
};
export const setHasDiscoveredSpaceMentions = () => {
    setCookie(`pplx.has-discovered-space-mentions`, 'true', {
        expires: MAX_DAYS,
    });
};
export const setTasksSettingsSeenCookie = (isSeen) => {
    setCookie(PPLX_TASKS_SETTINGS_SEEN, String(isSeen), {
        expires: MAX_DAYS,
    });
};
export const getPatentsTutorialDismissedCookie = () => {
    const cookie = getCookie(PPLX_PATENTS_TUTORIAL_DISMISSED);
    return cookie === 'true';
};
export const setPatentsTutorialDismissedCookie = (isDismissed) => {
    setCookie(PPLX_PATENTS_TUTORIAL_DISMISSED, String(isDismissed), {
        expires: MAX_DAYS,
    });
};
/**
 * Set session cookie for pc from URL param (e.g. mozilla default search)
 */
export const setDefaultSearchSourceCookie = (defaultSearchSource) => {
    setCookie(PPLX_DEFAULT_SEARCH_SESSION_COOKIE, defaultSearchSource, {
        expires: ONE_MONTH_IN_DAYS,
    });
};
export const getDefaultSearchSourceCookie = () => {
    return getCookie(PPLX_DEFAULT_SEARCH_SESSION_COOKIE);
};
/**
 * Set utm source cookie from URL param (e.g. partnerships)
 */
export const setUtmSourceCookie = (utmSource) => {
    setCookie(PPLX_UTM_SOURCE_COOKIE, utmSource);
};
export const getUtmSourceCookie = () => {
    return getCookie(PPLX_UTM_SOURCE_COOKIE);
};
export const getDynamicCopyCookie = () => {
    return getCookie(PPLX_DYNAMIC_COPY_COOKIE);
};
/**
 * Set session cookie for pt from URL param (dynamic copy for e.g. install gates)
 */
export const setDynamicCopyCookie = (dynamicCopy) => {
    setCookie(PPLX_DYNAMIC_COPY_COOKIE, dynamicCopy);
};
const AUTH_TAG = '[authHeaderToCookie]';
// TODO - rate limiting?
export const authHeaderToCookie = async (authHeader) => {
    try {
        if (!authHeader || !authHeader.startsWith('Bearer ')) {
            authHeaderFail('bad_auth_header');
            return null;
        }
        const csrfToken = getRandomCsrfToken();
        if (!csrfToken) {
            authHeaderFail('no_csrf_token');
            return null;
        }
        const signInUrl = getAbsoluteUrl({
            hostHeader: null,
            urlPath: `/api/auth/callback/pplx-jwt-to-cookie`,
            base: process.env.NEXTJS_AUTH_URL,
        });
        const toHash = `${csrfToken}${process.env.NEXTAUTH_SECRET}`;
        const hashed = await edgeCompatibleSHA256HexDigest(toHash);
        const csrfCookie = `next-auth.csrf-token=${csrfToken}%7C${hashed}`;
        const signInHeaders = {
            'content-type': 'application/x-www-form-urlencoded',
            authorization: authHeader,
            internal_request_secret: process.env.INTERNAL_REQUEST_SECRET ?? '',
            Cookie: `${csrfCookie}`,
        };
        const response = await fetch(signInUrl, {
            method: 'POST',
            headers: signInHeaders,
            body: new URLSearchParams({ csrfToken }),
            redirect: 'manual',
        });
        const setCookieHeaders = response.headers.getSetCookie();
        const sessionTokenCookie = setCookieHeaders.find((cookie) => cookie.includes('session-token'));
        if (sessionTokenCookie) {
            authHeaderSuccess();
            const parsed = parseCookieString(sessionTokenCookie);
            return parsed;
        }
        authHeaderFail('bad_exchange');
        return null;
    }
    catch (err) {
        authHeaderFail('errored_exchange');
        warnWithTime(`${AUTH_TAG} error:`, err);
        return null;
    }
};
const authHeaderSuccess = () => {
    logWithTime(`${AUTH_TAG} successful perplexity to jwt cookie exchange`);
    reportAction('ppl.nextjs.nextauth.auth_header_to_cookie', {
        status: 'success',
    });
};
const authHeaderFail = (reason) => {
    logWithTime(`${AUTH_TAG} failed perplexity jwt to cookie exchange: ${reason}`);
    reportAction('ppl.nextjs.nextauth.auth_header_to_cookie', {
        status: 'fail',
        reason,
    });
};
const getRandomCsrfToken = () => {
    const array = new Uint8Array(32);
    boundGetRandomValues(array);
    return array.reduce((str, byte) => str + byte.toString(16).padStart(2, '0'), '');
};
const parseCookieString = (cookieString) => {
    const [nameValue, ...options] = cookieString.split('; ');
    const [name, value] = nameValue?.split('=') ?? [];
    const parsedOptions = options.reduce((acc, option) => {
        const [key, val] = option.split('=');
        // next_response.cookies.set options are case sensitive
        const cleanedKey = COOKIE_OPTION_KEYS[key?.toLowerCase() ?? ''] ?? 'unknown';
        if (cleanedKey === 'expires') {
            acc[cleanedKey] = new Date(val ?? '');
        }
        else if (cleanedKey === 'unknown') {
            logWithTime(`[parseCookieString] unexpected cookie option: ${key}`);
        }
        else {
            acc[cleanedKey] = val || true;
        }
        return acc;
    }, {});
    return {
        name: decodeURIComponent(name ?? ''),
        value: decodeURIComponent(value ?? ''),
        options: parsedOptions,
    };
};
//# sourceMappingURL=cookieManager.js.map